import React, { useState, useEffect, useCallback } from 'react';
import { Users, Package, TrendingUp, DollarSign, Trash2, Eye, BarChart3 } from 'lucide-react';
import Header from '../components/Header';
import RevenueDashboard from '../components/RevenueDashboard';
import { api, useAuth } from '../App';
import { getCategoryLabel } from '../lib/categories';
import { toast } from 'sonner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

export default function AdminPage() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [usersPage, setUsersPage] = useState(1);
  const [listingsPage, setListingsPage] = useState(1);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      if (activeTab === 'overview' || !stats) {
        const { data } = await api.get('/admin/stats');
        setStats(data);
      }
      if (activeTab === 'users') {
        const { data } = await api.get(`/admin/users?page=${usersPage}&limit=20`);
        setUsers(data.users);
      }
      if (activeTab === 'listings') {
        const { data } = await api.get(`/admin/listings?page=${listingsPage}&limit=20`);
        setListings(data.listings);
      }
    } catch {
      // silently handled
    } finally {
      setLoading(false);
    }
  }, [activeTab, usersPage, listingsPage, stats]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user and all their listings?')) return;
    try {
      await api.delete(`/admin/users/${userId}`);
      toast.success('User deleted');
      fetchData();
    } catch (err) {
      toast.error('Failed to delete user');
    }
  };

  const handleDeleteListing = async (listingId) => {
    if (!window.confirm('Delete this listing?')) return;
    try {
      await api.delete(`/listings/${listingId}`);
      toast.success('Listing deleted');
      fetchData();
    } catch (err) {
      toast.error('Failed to delete listing');
    }
  };

  return (
    <div className="min-h-screen bg-surface-base">
      <Header />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="font-clash text-3xl sm:text-4xl font-medium text-white">
            Admin Dashboard
          </h1>
          <p className="text-zinc-400">Manage users, listings, and platform analytics</p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-surface-card border border-border-subtle p-1 mb-8">
            <TabsTrigger value="overview" className="data-[state=active]:bg-gold data-[state=active]:text-black">
              <BarChart3 className="w-4 h-4 mr-2" /> Overview
            </TabsTrigger>
            <TabsTrigger value="users" className="data-[state=active]:bg-gold data-[state=active]:text-black">
              <Users className="w-4 h-4 mr-2" /> Users
            </TabsTrigger>
            <TabsTrigger value="listings" className="data-[state=active]:bg-gold data-[state=active]:text-black">
              <Package className="w-4 h-4 mr-2" /> Listings
            </TabsTrigger>
            <TabsTrigger value="revenue" className="data-[state=active]:bg-gold data-[state=active]:text-black">
              <DollarSign className="w-4 h-4 mr-2" /> Revenue
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview">
            {loading ? (
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {[1,2,3,4].map(i => <div key={i} className="h-32 bg-surface-card animate-pulse" />)}
              </div>
            ) : stats && (
              <>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                  <div className="p-6 bg-surface-card border border-border-subtle">
                    <Users className="w-8 h-8 text-gold mb-3" />
                    <p className="text-3xl font-clash font-semibold text-white">
                      {stats.stats.total_users}
                    </p>
                    <p className="text-sm text-zinc-400">Total Users</p>
                  </div>
                  <div className="p-6 bg-surface-card border border-border-subtle">
                    <Package className="w-8 h-8 text-gold mb-3" />
                    <p className="text-3xl font-clash font-semibold text-white">
                      {stats.stats.total_listings}
                    </p>
                    <p className="text-sm text-zinc-400">Total Listings</p>
                  </div>
                  <div className="p-6 bg-surface-card border border-border-subtle">
                    <TrendingUp className="w-8 h-8 text-gold mb-3" />
                    <p className="text-3xl font-clash font-semibold text-white">
                      {stats.stats.active_listings}
                    </p>
                    <p className="text-sm text-zinc-400">Active Listings</p>
                  </div>
                  <div className="p-6 bg-surface-card border border-border-subtle">
                    <DollarSign className="w-8 h-8 text-gold mb-3" />
                    <p className="text-3xl font-clash font-semibold text-white">
                      {stats.stats.sold_listings}
                    </p>
                    <p className="text-sm text-zinc-400">Items Sold</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  <div className="p-6 bg-surface-card border border-border-subtle">
                    <h3 className="font-clash text-lg font-medium text-white mb-4">Recent Listings</h3>
                    <div className="space-y-3">
                      {stats.recent_listings?.map(listing => (
                        <div key={listing.id} className="flex items-center justify-between py-2 border-b border-border-subtle last:border-0">
                          <div>
                            <p className="text-white text-sm">{listing.title}</p>
                            <p className="text-xs text-zinc-500">${listing.price?.toLocaleString()}</p>
                          </div>
                          <span className={`px-2 py-0.5 text-xs ${listing.is_active ? 'badge-trade' : 'badge-auction'}`}>
                            {listing.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="p-6 bg-surface-card border border-border-subtle">
                    <h3 className="font-clash text-lg font-medium text-white mb-4">Recent Users</h3>
                    <div className="space-y-3">
                      {stats.recent_users?.map(u => (
                        <div key={u.id || u.email} className="flex items-center justify-between py-2 border-b border-border-subtle last:border-0">
                          <div>
                            <p className="text-white text-sm">{u.name}</p>
                            <p className="text-xs text-zinc-500">{u.email}</p>
                          </div>
                          <span className={`px-2 py-0.5 text-xs ${u.role === 'admin' ? 'badge-auction' : 'badge-trade'}`}>
                            {u.role}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </>
            )}
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users">
            {loading ? (
              <div className="space-y-2">
                {[1,2,3,4,5].map(i => <div key={i} className="h-16 bg-surface-card animate-pulse" />)}
              </div>
            ) : (
              <div className="bg-surface-card border border-border-subtle overflow-hidden">
                <table className="w-full">
                  <thead className="bg-surface-elevated">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">User</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">Role</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">Joined</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">Rating</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-zinc-400 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border-subtle">
                    {users.map(u => (
                      <tr key={u.id} className="hover:bg-surface-elevated">
                        <td className="px-4 py-4">
                          <div>
                            <p className="text-white font-medium">{u.name}</p>
                            <p className="text-sm text-zinc-400">{u.email}</p>
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <span className={`px-2 py-1 text-xs ${u.role === 'admin' ? 'badge-auction' : 'badge-trade'}`}>
                            {u.role}
                          </span>
                        </td>
                        <td className="px-4 py-4 text-sm text-zinc-400">
                          {new Date(u.created_at).toLocaleDateString()}
                        </td>
                        <td className="px-4 py-4 text-sm text-zinc-400">
                          {u.rating?.toFixed(1) || '0.0'} ({u.review_count || 0})
                        </td>
                        <td className="px-4 py-4 text-right">
                          {u.role !== 'admin' && (
                            <button
                              onClick={() => handleDeleteUser(u.id)}
                              className="p-2 text-zinc-400 hover:text-red-500"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </TabsContent>

          {/* Listings Tab */}
          <TabsContent value="listings">
            {loading ? (
              <div className="space-y-2">
                {[1,2,3,4,5].map(i => <div key={i} className="h-16 bg-surface-card animate-pulse" />)}
              </div>
            ) : (
              <div className="bg-surface-card border border-border-subtle overflow-hidden">
                <table className="w-full">
                  <thead className="bg-surface-elevated">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">Listing</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">Category</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">Price</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">Status</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-zinc-400 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border-subtle">
                    {listings.map(listing => (
                      <tr key={listing.id} className="hover:bg-surface-elevated">
                        <td className="px-4 py-4">
                          <div>
                            <p className="text-white font-medium">{listing.title}</p>
                            <p className="text-sm text-zinc-400">by {listing.seller_name}</p>
                          </div>
                        </td>
                        <td className="px-4 py-4 text-sm text-zinc-400">
                          {getCategoryLabel(listing.category)}
                        </td>
                        <td className="px-4 py-4 text-sm text-white">
                          ${listing.price?.toLocaleString()}
                        </td>
                        <td className="px-4 py-4">
                          {listing.is_sold ? (
                            <span className="badge-buy-now px-2 py-1 text-xs">Sold</span>
                          ) : listing.is_active ? (
                            <span className="badge-trade px-2 py-1 text-xs">Active</span>
                          ) : (
                            <span className="badge-auction px-2 py-1 text-xs">Inactive</span>
                          )}
                        </td>
                        <td className="px-4 py-4 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <a
                              href={`/listing/${listing.id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="p-2 text-zinc-400 hover:text-white"
                            >
                              <Eye className="w-4 h-4" />
                            </a>
                            <button
                              onClick={() => handleDeleteListing(listing.id)}
                              className="p-2 text-zinc-400 hover:text-red-500"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </TabsContent>

          {/* Revenue Tab */}
          <TabsContent value="revenue">
            <RevenueDashboard />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
